/**
 * The drag payload carried by a block tile in the Add panel's Blocks accordion (C2 — #22207).
 * Whichever canvas drop handler lands next (C3) reads this to look up the matching
 * PresentationBlockOption and build the placed block via createPresentationNextBlock.
 *
 * Mirrors sectionDragPayload.ts's shape. Unlike a section layout, a block `type` is unique in the
 * catalog (one entry per type — see getPresentationBlockOptions), so the payload can carry `type`
 * directly rather than needing a separate `optionKey`.
 *
 * Security note for C3: `type` here is untrusted client input — this module doesn't (and can't)
 * enforce the Blocks accordion's superadmin gate (blockCatalogGroups.ts's SUPERADMIN_ONLY_BLOCK_TYPES),
 * since DataTransfer is fully attacker-controllable. The drop handler must re-check the acting user's
 * role against the dropped `type` before persisting, not just trust that the UI hid the tile.
 */
export interface PresentationBlockDragPayload {
  type: string
  label: string
}

/** Custom MIME type on the drag event's DataTransfer carrying the JSON-encoded payload above. */
export const PRESENTATION_BLOCK_DRAG_MIME_TYPE = 'application/x-paintscout-presentation-block'

/** The `data-*` attribute a canvas drop zone can use to tell a block drag from a section drag. */
export const PRESENTATION_BLOCK_DRAG_KIND = 'block'

export function setBlockDragPayload(dataTransfer: DataTransfer, payload: PresentationBlockDragPayload) {
  dataTransfer.effectAllowed = 'copy'
  dataTransfer.setData(PRESENTATION_BLOCK_DRAG_MIME_TYPE, JSON.stringify(payload))
  // fallback for any drop target that only reads plain text
  dataTransfer.setData('text/plain', payload.label)
}
